import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { Cascader, Tabs, Tooltip } from 'antd'
import { useSelector } from 'dva'
import LevelTextIcon from '@/components/LevelTextIcon'
import styles from '../../index.less'
import { DvaState } from '@/types/react-redux'

interface Props {
  initialValue: string[]
  platform: string
  onChange: (ruleIds: string[], ruleNames: string[]) => void
}

export type RuleNodeType = {
  value: string
  text: string
  label: React.ReactNode
  level: 1 | 2 | 3
}

export type GroupType = {
  value: string
  label: React.ReactNode
  text: string
  platform: string
  children: GroupType[]
}

export type GroupMapType = {
  // valueMap用于将后端返回的数据转换成Cascader组件需要的数据格式
  valueMap: Map<string, string[]>
  // flatMap用于将Cascader组件的value转换成后端需要的数据格式
  flatMap: Map<string, { value: string; label: string }[]>
}

type LevelType = 0 | 1 | 2 | 3

const LEVEL_INPUT_MAP = {
  初: 1,
  中: 2,
  高: 3,
}

export const formatRule = (rule: any): RuleNodeType => ({
  value: rule.id,
  text: rule.name,
  label: (
    <>
      <span>{rule.name}</span>
      <LevelTextIcon level={rule.alert_level} style={{ marginLeft: 8 }} />
    </>
  ),
  level: rule.alert_level,
})

export const formatGroups = (list: any[]): GroupType[] => {
  return (
    list?.map(i => {
      const children = i.rules
        ? i.rules.map(formatRule)
        : formatGroups(i.groups || i.sub_groups || [])
      return {
        value: i.id || i.qc_norm_id,
        label: i.name || i.qc_norm_name,
        text: i.name || i.qc_norm_name,
        platform: i.platform,
        children,
      }
    }) || []
  )
}

export default function RuleCascader(props: Props) {
  const { initialValue, platform, onChange } = props
  const { groups, allGroupsMap: allMap } = useSelector((state: DvaState) => state.alarmRules)
  const loading = useSelector(store => store.loading.effects['alarmRules/fetchGroups'])
  const [value, setValue] = useState<string[][]>([])
  const [level, setLevel] = useState<LevelType>(0)

  useEffect(() => {
    if (!initialValue || initialValue.length === 0 || allMap.valueMap.size === 0) {
      setValue([])
      return
    }

    setValue(initialValue.map(v => allMap.valueMap.get(v) || []))
  }, [allMap, initialValue])

  const rules = useMemo(
    () =>
      value
        .map(v => v[v.length - 1])
        .filter(v => allMap.flatMap.has(v))
        .map(v => allMap.flatMap.get(v) || [])
        .flat(),
    [value, allMap]
  )

  const renderDropdown = (menu: ReactElement) => (
    <div>
      <Tabs
        defaultActiveKey="0"
        onChange={key => setLevel(Number(key) as LevelType)}
        tabBarStyle={{ paddingLeft: 20, paddingRight: 20, marginBottom: 5 }}
      >
        <Tabs.TabPane tab="全部" key="0">
          {menu}
        </Tabs.TabPane>
        <Tabs.TabPane tab="高级告警" key="3">
          {menu}
        </Tabs.TabPane>
        <Tabs.TabPane tab="中级告警" key="2">
          {menu}
        </Tabs.TabPane>
        <Tabs.TabPane tab="初级告警" key="1">
          {menu}
        </Tabs.TabPane>
      </Tabs>
    </div>
  )

  const renderMaxTag = () => (
    <Tooltip
      title={<div className={styles.tooltipTitle}>{rules.map(v => v.label).join('、')}</div>}
    >
      {`+${rules.length} ...`}
    </Tooltip>
  )

  return (
    <Cascader
      multiple
      value={value}
      maxTagCount={0}
      loading={loading}
      disabled={loading}
      style={{ width: 135 }}
      placeholder="请选择质检项"
      className={styles.ruleInput}
      dropdownClassName={styles.ruleDropdown}
      maxTagPlaceholder={renderMaxTag}
      showCheckedStrategy={Cascader.SHOW_CHILD}
      options={groups[level]?.filter(g => g.platform === platform)}
      showSearch={{
        filter: (inputValue, path) =>
          (path as RuleNodeType[]).some(option => {
            if (option.text?.includes(inputValue)) return true
            const firstText = inputValue.at(0)
            if (firstText && LEVEL_INPUT_MAP[firstText]) {
              return option.level === LEVEL_INPUT_MAP[firstText]
            }

            return false
          }),
      }}
      onBlur={() => {
        const ruleIds = rules.map(v => v.value)
        const ruleNames = rules.map(v => v.label)
        onChange(ruleIds, ruleNames)
      }}
      onChange={(val: any) => {
        setValue(val)
        if (val.length === 0) {
          onChange([], [])
        }
      }}
      displayRender={(label, selectedOptions) => {
        const selectedLen = selectedOptions?.length
        if (selectedLen && selectedOptions[selectedLen - 1]) {
          return selectedOptions[selectedLen - 1].text
        }

        const labelLen = label.length
        const labelValue = allMap.flatMap.get(label[labelLen - 1])
        if (labelValue) {
          return labelValue[0].label
        }
      }}
      dropdownRender={renderDropdown}
    />
  )
}
